import React, { useMemo, useState } from 'react';

export default function InfluencerProbe() {
  const [platform, setPlatform] = useState('instagram');
  const [input, setInput] = useState(''); // handle or URL
  const [igBizId, setIgBizId] = useState(''); // source IG business account id (who authorized our app)
  const [igToken, setIgToken] = useState(''); // long-lived access token
  const [appId, setAppId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [shortToken, setShortToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [raw, setRaw] = useState('');
  const [parsed, setParsed] = useState(null);
  const [error, setError] = useState('');

  const buildTargetUrl = () => {
    const val = (input || '').trim();
    if (!val) return null;
    if (platform === 'instagram') {
      let handle = val;
      const m = val.match(/instagram\.com\/(?:reel|p|tv|stories|reels|reel\/|p\/|tv\/)?([A-Za-z0-9_.]+)/i);
      if (m && m[1]) handle = m[1].replace(/\/$/, '');
      // Try multiple known endpoints (best-effort). Some may require headers/cookies; via proxy we might still get JSON for some.
      const candidates = [
        // New JSON endpoints first (often return data without full HTML)
        `https://i.instagram.com/api/v1/users/web_profile_info/?username=${handle}`,
        `https://www.instagram.com/api/v1/users/web_profile_info/?username=${handle}`,
        // Legacy/alt params
        `https://www.instagram.com/${handle}/?__a=1&__d=dis`,
        // Plain profile page (may be gated)
        `https://www.instagram.com/${handle}/`,
      ];
      return candidates;
    }
    if (platform === 'instagram-graph') {
      const handle = val.replace(/^@/, '');
      if (!igBizId || !igToken || !handle) return null;
      const fields = encodeURIComponent(
        `business_discovery.username(${handle}){id,username,name,biography,followers_count,follows_count,media_count,profile_picture_url,media.limit(10){id,caption,comments_count,like_count,timestamp,permalink,media_type,media_url}}
        `
      );
      // Business Discovery requires the source IG Business Account ID
      const url = `https://graph.facebook.com/v21.0/${igBizId}?fields=${fields}&access_token=${encodeURIComponent(igToken)}`;
      return [url];
    }
    if (platform === 'youtube') {
      let path = val;
      const m1 = val.match(/youtube\.com\/(?:@[^/?#]+|channel\/[^/?#]+)/i);
      if (m1) path = m1[0].replace('https://', '').replace('http://', '');
      if (!/^https?:\/\//i.test(path)) path = `https://www.${path.startsWith('youtube.com') ? '' : 'youtube.com/'}${path}`;
      return [path.endsWith('/about') ? path : `${path.replace(/\/?$/, '/') }about`];
    }
    if (platform === 'tiktok') {
      let handle = val;
      const m = val.match(/tiktok\.com\/@([A-Za-z0-9_.]+)/i);
      if (m && m[1]) handle = `https://www.tiktok.com/@${m[1]}`; else if (!/^https?:\/\//.test(handle)) handle = `https://www.tiktok.com/@${handle}`;
      return [handle];
    }
    return null;
  };

  const proxyUrl = (u) => {
    if (!u) return null;
    if (u.startsWith('https://')) return `https://r.jina.ai/${u}`;
    if (u.startsWith('http://')) return `https://r.jina.ai/${u}`;
    return `https://r.jina.ai/https://${u}`;
  };

  const parseInstagram = (text) => {
    try {
      let jsonStart = text.indexOf('{');
      if (jsonStart >= 0) {
        const maybe = text.slice(jsonStart);
        try {
          const j = JSON.parse(maybe);
          const g = j?.graphql?.user || j?.data?.user || null;
          if (g) {
            return {
              username: g.username,
              full_name: g.full_name,
              followers: g.edge_followed_by?.count ?? null,
              following: g.edge_follow?.count ?? null,
              posts: g.edge_owner_to_timeline_media?.count ?? null,
              biography: g.biography || ''
            };
          }
        } catch (_) {}
      }
      const followers = /"edge_followed_by":\{"count":(\d+)\}/.exec(text)?.[1];
      const following = /"edge_follow":\{"count":(\d+)\}/.exec(text)?.[1];
      const posts = /"edge_owner_to_timeline_media":\{"count":(\d+)\}/.exec(text)?.[1];
      const username = /"username":"([^"]+)"/.exec(text)?.[1];
      const fullName = /"full_name":"([^"]*)"/.exec(text)?.[1];
      const bio = /"biography":"([^"]*)"/.exec(text)?.[1];
      if (followers || following || posts) {
        return {
          username,
          full_name: fullName,
          followers: followers ? Number(followers) : null,
          following: following ? Number(following) : null,
          posts: posts ? Number(posts) : null,
          biography: bio ? decodeURIComponent(bio) : ''
        };
      }
    } catch {}
    return null;
  };

  const parseInstagramGraph = (text) => {
    try {
      const j = JSON.parse(text);
      const bd = j?.business_discovery;
      if (!bd) return j; // show raw if structure differs
      return {
        id: bd.id,
        username: bd.username,
        name: bd.name,
        followers_count: bd.followers_count,
        follows_count: bd.follows_count,
        media_count: bd.media_count,
        biography: bd.biography,
        profile_picture_url: bd.profile_picture_url,
        top_media: (bd.media?.data || []).map(m => ({ id: m.id, caption: m.caption, likes: m.like_count, comments: m.comments_count, ts: m.timestamp, url: m.permalink || m.media_url }))
      };
    } catch (e) {
      return null;
    }
  };

  const parseYouTube = (text) => {
    const subs = /"subscriberCountText"\s*:\s*\{[^}]*"simpleText"\s*:\s*"([^"]+)"/i.exec(text)?.[1]
      || /(\d[\d., ]+\s*(?:K|M|B)?\s*subscribers)/i.exec(text)?.[1];
    const joined = /Joined\s*([^<\n]+)/i.exec(text)?.[1];
    const views = /(\d[\d., ]+)\s+views/i.exec(text)?.[1];
    const title = /<meta\s+property="og:title"\s+content="([^"]+)"/i.exec(text)?.[1];
    return { title, subscribers: subs || null, views: views || null, joined: joined || null };
  };

  const parseTikTok = (text) => {
    const name = /<title>([^<]+)<\/title>/i.exec(text)?.[1];
    const followers = /(\d[\d.,KkMm]+)\s*Followers/i.exec(text)?.[1];
    const following = /(\d[\d.,KkMm]+)\s*Following/i.exec(text)?.[1];
    const likes = /(\d[\d.,KkMm]+)\s*Likes/i.exec(text)?.[1];
    return { name, followers, following, likes };
  };

  const onFetch = async () => {
    setError('');
    setRaw('');
    setParsed(null);
    const urls = buildTargetUrl();
    if (!urls || urls.length === 0) { setError('Gerekli alanları doldurun.'); return; }
    setLoading(true);
    for (const u of urls) {
      try {
        const proxied = platform === 'instagram-graph' ? proxyUrl(u) : proxyUrl(u);
        const res = await fetch(proxied, { method: 'GET' });
        const txt = await res.text();
        setRaw(txt);
        let p = null;
        if (platform === 'instagram') p = parseInstagram(txt);
        else if (platform === 'instagram-graph') p = parseInstagramGraph(txt);
        else if (platform === 'youtube') p = parseYouTube(txt);
        else if (platform === 'tiktok') p = parseTikTok(txt);
        if (p && (Object.values(p).some(Boolean))) { setParsed(p); break; }
      } catch (e) {
        setError(String(e));
      }
    }
    setLoading(false);
  };

  const Example = useMemo(() => {
    if (platform === 'instagram') return 'https://www.instagram.com/instagram/';
    if (platform === 'instagram-graph') return 'hedef kullanıcı adı (ör: nike)';
    if (platform === 'youtube') return 'https://www.youtube.com/@YouTubeCreators';
    if (platform === 'tiktok') return 'https://www.tiktok.com/@scout2015';
    return '';
  }, [platform]);

  const onExchangeLongLived = async () => {
    setError('');
    if (!appId || !appSecret || !shortToken) { setError('App ID, App Secret ve kısa süreli token gerekli.'); return; }
    try {
      const url = `https://graph.facebook.com/v21.0/oauth/access_token?grant_type=fb_exchange_token&client_id=${encodeURIComponent(appId)}&client_secret=${encodeURIComponent(appSecret)}&fb_exchange_token=${encodeURIComponent(shortToken)}`;
      const res = await fetch(proxyUrl(url));
      const txt = await res.text();
      setRaw(txt);
      const j = JSON.parse(txt);
      if (j.access_token) {
        setIgToken(j.access_token);
        setParsed({ exchanged: true, token_type: j.token_type, expires_in: j.expires_in });
      } else {
        setParsed(j);
      }
    } catch (e) {
      setError(String(e));
    }
  };

  const onDebugToken = async () => {
    setError('');
    const tokenToDebug = igToken || shortToken;
    if (!appId || !appSecret || !tokenToDebug) { setError('App ID, App Secret ve doğrulanacak token gerekli.'); return; }
    try {
      const appToken = `${appId}|${appSecret}`;
      const url = `https://graph.facebook.com/v21.0/debug_token?input_token=${encodeURIComponent(tokenToDebug)}&access_token=${encodeURIComponent(appToken)}`;
      const res = await fetch(proxyUrl(url));
      const txt = await res.text();
      setRaw(txt);
      setParsed(JSON.parse(txt));
    } catch (e) {
      setError(String(e));
    }
  };

  const onFetchAccountInfo = async () => {
    setError('');
    if (!igBizId || !igToken) { setError('IG Business Account ID ve Access Token gerekli.'); return; }
    try {
      const fields = encodeURIComponent('id,username,followers_count,follows_count,media_count,profile_picture_url,biography');
      const url = `https://graph.facebook.com/v21.0/${igBizId}?fields=${fields}&access_token=${encodeURIComponent(igToken)}`;
      const res = await fetch(proxyUrl(url));
      const txt = await res.text();
      setRaw(txt);
      setParsed(JSON.parse(txt));
    } catch (e) {
      setError(String(e));
    }
  };

  const onScrapePW = async () => {
    try {
      setError('');
      setRaw('');
      setParsed(null);
      const val = (input || '').trim();
      if (!val) { setError('URL veya kullanıcı adı gerekli.'); return; }
      setLoading(true);
      const resp = await fetch('/api/labs/ig-scrape-pw', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urlOrUsername: val })
      });
      const txt = await resp.text();
      setRaw(txt);
      try {
        const js = JSON.parse(txt);
        setParsed(js);
      } catch (_) {
        setParsed(null);
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Influencer Probe (Experimental)</h1>
        <p className="text-sm text-gray-600 mt-1">
          Hızlı deneme: Public fetch (proxy) ile Instagram/YouTube/TikTok sayfalarından veya Instagram Graph API'den temel metrikleri çekmeyi dener.
          Instagram Graph seçeneği için IG Business Account ID ve Access Token girmeniz gerekir.
        </p>
      </div>

      <div className="bg-white rounded-xl border p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Platform</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm"
            >
              <option value="instagram">Instagram (public page)</option>
              <option value="instagram-graph">Instagram Graph API (token)</option>
              <option value="youtube">YouTube (public)</option>
              <option value="tiktok">TikTok (public)</option>
            </select>
          </div>
          <div className={platform === 'instagram-graph' ? 'md:col-span-1' : 'md:col-span-2'}>
            <label className="block text-xs text-gray-600 mb-1">{platform === 'instagram-graph' ? 'Target Username' : 'Kullanıcı adı veya URL'}</label>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={Example}
              className="w-full border rounded-md px-3 py-2 text-sm"
            />
          </div>
          {platform === 'instagram-graph' && (
            <>
              <div>
                <label className="block text-xs text-gray-600 mb-1">IG Business Account ID</label>
                <input
                  value={igBizId}
                  onChange={(e) => setIgBizId(e.target.value)}
                  placeholder="1784*********"
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Access Token</label>
                <input
                  value={igToken}
                  onChange={(e) => setIgToken(e.target.value)}
                  placeholder="EAAG... (long‑lived)"
                  className="w-full border rounded-md px-3 py-2 text-sm"
                />
              </div>
            </>
          )}
          <div className="flex gap-2">
            {platform !== 'instagram' ? (
              <button
                onClick={onFetch}
                disabled={loading}
                className={`px-4 py-2 rounded-md text-white ${loading ? 'bg-gray-400' : 'bg-blue-600'} `}
              >{loading ? 'Fetching…' : 'Fetch'}</button>
            ) : (
              <button
                onClick={onScrapePW}
                disabled={loading}
                className={`px-4 py-2 rounded-md text-white ${loading ? 'bg-gray-400' : 'bg-purple-700'} `}
              >{loading ? 'Working…' : 'Scrape'}</button>
            )}
          </div>
        </div>
        {error ? <div className="mt-3 text-sm text-red-600">{error}</div> : null}
      </div>

      {/* Cookie header alanı kaldırıldı; tek buton (Scrape) ile headless backend çağrısı yapılır */}
      {platform === 'instagram-graph' && (
        <div className="bg-white rounded-xl border p-4 mb-6">
          <div className="text-sm font-medium text-gray-800 mb-3">Instagram Graph Token Tools</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-600 mb-1">App ID</label>
              <input value={appId} onChange={(e) => setAppId(e.target.value)} placeholder="1234567890" className="w-full border rounded-md px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">App Secret</label>
              <input value={appSecret} onChange={(e) => setAppSecret(e.target.value)} placeholder="xxxxxxxx" className="w-full border rounded-md px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Short‑lived Token</label>
              <input value={shortToken} onChange={(e) => setShortToken(e.target.value)} placeholder="EAAG..." className="w-full border rounded-md px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-2">
              <button onClick={onExchangeLongLived} className="px-3 py-2 rounded-md bg-emerald-600 text-white text-sm">Exchange → Long‑lived</button>
              <button onClick={onDebugToken} className="px-3 py-2 rounded-md bg-gray-700 text-white text-sm">Debug Token</button>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={onFetchAccountInfo} className="px-3 py-2 rounded-md bg-indigo-600 text-white text-sm">Fetch Account Info (by IG Biz ID)</button>
            <button onClick={onFetch} className="px-3 py-2 rounded-md bg-blue-600 text-white text-sm">Business Discovery (by username)</button>
          </div>
          <div className="mt-3 text-xs text-gray-500">
            Dikkat: Token işlemleri demo amaçlıdır. Üretimde bu adımlar backend proxy üzerinden yapılmalıdır.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border p-4">
          <div className="text-sm font-medium text-gray-800 mb-2">Extracted / Parsed</div>
          {parsed ? (
            <pre className="text-xs bg-gray-50 rounded p-3 overflow-auto max-h-96">{JSON.stringify(parsed, null, 2)}</pre>
          ) : (
            <div className="text-sm text-gray-500">Henüz veri yok veya parse edilemedi.</div>
          )}
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="text-sm font-medium text-gray-800 mb-2">Raw (truncated)</div>
          <pre className="text-xs bg-gray-50 rounded p-3 overflow-auto max-h-96 whitespace-pre-wrap break-all">
            {raw ? raw.slice(0, 20000) : '—'}
          </pre>
        </div>
      </div>

      <div className="mt-6 text-xs text-gray-500 space-y-1">
        <div>
          <div className="mb-1">Instagram Graph Business Discovery örneği:</div>
          <pre className="bg-gray-50 border rounded p-2 overflow-auto text-[11px]">
            <code>{`GET https://graph.facebook.com/v21.0/{ig-business-id}?` +
`fields=business_discovery.username({username}){id,username,name,biography,followers_count,follows_count,media_count,profile_picture_url,media{ id,caption,comments_count,like_count,timestamp,permalink,media_type,media_url }}&access_token=YOUR_TOKEN`}</code>
          </pre>
        </div>
        <p>Not: Access Token’ı frontend’e koymak yalnızca PoC içindir; üretimde token yönetimi backend tarafında yapılmalıdır.</p>
      </div>
    </div>
  );
}
